import { type FC, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type DateRange } from "react-day-picker";
import { X } from "lucide-react";
import { Button } from "~/shared/ui/button";
import { Calendar } from "~/shared/ui/calendar";
import { Select, SelectContent, SelectItem } from "~/shared/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "~/shared/ui/popover";
import { cn } from "~/shared/lib/utils";
import { FILTER_FIELD_CLASS, FilterSelectTrigger } from "./filter-field-primitives";
import { DATE_RANGE_PRESETS, normalizeRange, presetLabel } from "./date-range-from-preset";
import {
    buildCustomRangeValue,
    classifySelectChange,
    extractPendingRange,
    formatDateRangeDisplayLabel,
    resolveSelectValue,
} from "./date-range-filter-helpers";

export const DateRangeFilter: FC<{
    value: string;
    onChange: (value: string | undefined) => void;
    labels?: { custom?: string; from?: string; to?: string };
    hasValue?: boolean;
}> = ({ value, onChange, labels, hasValue }) => {
    const { t: tr } = useTranslation("ui");
    const [customOpen, setCustomOpen] = useState(false);
    const [pendingRange, setPendingRange] = useState<DateRange | undefined>(undefined);

    const isCustom = value?.startsWith("custom:");
    const presetKey = value?.startsWith("preset:") ? value.slice(7) : "";

    const localizedPreset = (key: string) =>
        tr(`dateRange.preset.${key}`, { defaultValue: presetLabel(key) ?? key });

    const displayLabel = useMemo(
        () =>
            formatDateRangeDisplayLabel({
                value,
                isCustom,
                presetKey,
                labels,
                localizedPreset,
                customFallback: tr("dateRange.custom", { defaultValue: "Custom range…" }),
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [value, isCustom, presetKey, labels, tr],
    );

    const openCustomPicker = () => {
        setPendingRange(extractPendingRange(value));
        setCustomOpen(true);
    };

    const applyCustom = () => {
        onChange(buildCustomRangeValue(pendingRange));
        setCustomOpen(false);
    };

    const handleSelectChange = (v: string) => {
        const change = classifySelectChange(v);
        if (change.action === "custom") {
            openCustomPicker();
            return;
        }
        onChange(change.action === "preset" ? `preset:${change.presetValue}` : undefined);
        setCustomOpen(false);
    };

    return (
        <div className="relative">
            <Select value={resolveSelectValue(isCustom, presetKey)} onValueChange={handleSelectChange}>
                <FilterSelectTrigger
                    className={cn(
                        "h-9 !w-full focus-visible:border-input focus-visible:ring-0",
                        FILTER_FIELD_CLASS,
                        hasValue && "pr-7",
                    )}
                >
                    <span className="min-w-0 flex-1 truncate text-left text-sm">{displayLabel}</span>
                </FilterSelectTrigger>
                <SelectContent position="popper" align="start">
                    <SelectItem value="__all__">{tr("dateRange.all", { defaultValue: "—" })}</SelectItem>
                    {DATE_RANGE_PRESETS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                            {localizedPreset(p.value)}
                        </SelectItem>
                    ))}
                    <SelectItem value="__custom__">
                        {labels?.custom ?? tr("dateRange.custom", { defaultValue: "Custom range…" })}
                    </SelectItem>
                </SelectContent>
            </Select>
            {hasValue && (
                <button
                    type="button"
                    className="absolute top-[18px] right-2 z-10 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-foreground"
                    onClick={() => {
                        onChange(undefined);
                        setCustomOpen(false);
                    }}
                >
                    <X className="size-3.5" />
                </button>
            )}

            {/* Custom range popover with visual calendar */}
            <Popover open={customOpen} onOpenChange={setCustomOpen}>
                <PopoverTrigger asChild>
                    <span aria-hidden className="pointer-events-none absolute inset-x-0 top-full" />
                </PopoverTrigger>
                <PopoverContent className="w-auto overflow-hidden p-0" align="start" sideOffset={4}>
                    <div className="flex flex-col">
                        <Calendar
                            mode="range"
                            captionLayout="dropdown"
                            selected={pendingRange}
                            onSelect={(range) => setPendingRange(normalizeRange(range))}
                            numberOfMonths={2}
                            autoFocus
                            disabled={{ after: new Date() }}
                        />
                        <div className="flex justify-end gap-2 border-t border-border px-3 py-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setCustomOpen(false)}
                                type="button"
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={applyCustom}
                                disabled={!pendingRange?.from && !pendingRange?.to}
                                type="button"
                            >
                                OK
                            </Button>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};
